import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const topics = [
  {
    icon: 'ri-information-line',
    question: 'What is window tinting?',
    answer:
      'Window tinting is the application of a thin polyester film to the interior surface of glass. The film contains UV inhibitors, dyes, or metal particles that block solar heat, reduce glare, and filter harmful UV radiation — all while maintaining visibility.',
  },
  {
    icon: 'ri-heart-pulse-line',
    question: 'What are the benefits of window tinting?',
    answer:
      'Window tinting offers: up to 77% solar heat rejection, 99%+ UV ray blocking (which prevents skin damage and interior fading), significant glare reduction, enhanced privacy, improved vehicle appearance, and lower cooling costs for homes and offices.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    question: 'How much does window tinting cost?',
    answer:
      'Automotive tinting typically ranges from $150–$600+ depending on vehicle size, film type, and coverage. Residential tinting is priced per square foot. PPF and ceramic coatings vary by vehicle size and coverage area. Contact us for a free, no-obligation quote.',
  },
  {
    icon: 'ri-tools-line',
    question: 'How does the installation process work?',
    answer:
      'We clean the glass thoroughly, precisely cut the film to match your windows, apply it using a professional squeegee technique to remove all air bubbles, and allow proper curing time. Most automotive jobs take 2–4 hours and are ready same day.',
  },
  {
    icon: 'ri-timer-line',
    question: 'How long does window tint last?',
    answer:
      'With premium films from brands like 3M, LLumar, and XPEL, window tint typically lasts 10–25 years. Our installations are backed by a lifetime warranty covering peeling, bubbling, and color change.',
  },
  {
    icon: 'ri-sun-line',
    question: 'Does tint protect against UV rays?',
    answer:
      'Yes — high-quality window film blocks 99% or more of harmful UV-A and UV-B radiation. This protects your skin during drives and prevents interior surfaces like leather, dashboards, and fabrics from fading and cracking over time.',
  },
];

export function AIAnswerSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-28 bg-[#0A0A0A] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 50%, #FF6B35 1px, transparent 1px), radial-gradient(circle at 70% 50%, #FF6B35 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#FF6B35]/5 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} grid grid-cols-1 lg:grid-cols-5 gap-16 items-start`}>
          {/* Left: sticky heading */}
          <div className="lg:col-span-2 lg:sticky lg:top-28">
            <span className="luxury-divider" />
            <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">
              Knowledge Center
            </span>
            <h2
              className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-6"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Window Tinting —
              <br />
              <span className="text-[#FF6B35]">What You Need</span>
              <br />
              to Know
            </h2>
            <p className="text-white/50 text-sm leading-relaxed mb-8">
              Clear, expert answers to the questions homeowners, vehicle owners, and commercial clients ask most often.
            </p>
            <a
              href="#faq"
              className="inline-flex items-center gap-2 text-[#FF6B35] text-sm font-semibold hover:gap-4 transition-all cursor-pointer"
            >
              View Full FAQ <i className="ri-arrow-right-line" />
            </a>
          </div>

          {/* Right: accordion answers */}
          <div className="lg:col-span-3 space-y-3">
            {topics.map((topic, i) => {
              const open = activeIndex === i;
              return (
                <div
                  key={i}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden cursor-pointer ${
                    open
                      ? 'bg-white/5 border-[#FF6B35]/40'
                      : 'bg-white/[0.03] border-white/8 hover:border-white/15'
                  }`}
                  onClick={() => setActiveIndex(open ? null : i)}
                >
                  <div className="flex items-center gap-4 p-5">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#FF6B35]/15 rounded-xl flex-shrink-0">
                      <i className={`${topic.icon} text-[#FF6B35] text-base`} />
                    </div>
                    <span
                      className="flex-1 text-sm font-semibold text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {topic.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-white/40 text-lg transition-transform duration-300 ${
                        open ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                  {open && (
                    <div className="px-5 pb-5 pl-[calc(1.25rem+2.25rem+1rem)]">
                      <p className="text-white/60 text-sm leading-relaxed">{topic.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats strip */}
        <div
          className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-400' : ''} mt-20 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden`}
        >
          {[
            { value: '99%+', label: 'UV Rays Blocked' },
            { value: '77%', label: 'Solar Heat Rejected' },
            { value: '30%', label: 'Energy Cost Reduction' },
            { value: '10–25yrs', label: 'Tint Lifespan' },
          ].map((stat) => (
            <div key={stat.value} className="bg-white/[0.03] px-8 py-7 text-center">
              <div
                className="text-2xl font-bold text-[#FF6B35] tracking-tight mb-1"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {stat.value}
              </div>
              <div className="text-xs text-white/40 uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
